import React, { useState } from 'react';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import FreeAccessNotice from '../../../components/FreeAccessNotice';

const RegistrationForm = ({ onSubmit, isLoading }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    displayName: '',
    accountType: 'free',
    agreeToTerms: false,
    // Studio-specific fields
    studioName: '',
    studioWebsite: '',
    studioDescription: '',
    teamSize: ''
  });

  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const validateForm = () => {
    const newErrors = {};

    // Email validation
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Password validation
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters long';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.password)) {
      newErrors.password = 'Password must contain uppercase, lowercase, and number';
    }

    // Confirm password validation
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    // Studio-specific validation
    if (formData.accountType === 'studio') {
      if (!formData.studioName || formData.studioName.trim() === '') {
        newErrors.studioName = 'Studio name is required';
      }

      if (formData.studioWebsite && !/^https?:\/\/.+\..+/.test(formData.studioWebsite)) {
        newErrors.studioWebsite = 'Please enter a valid URL';
      }

      if (!formData.studioDescription || formData.studioDescription.trim() === '') {
        newErrors.studioDescription = 'Studio description is required';
      } else if (formData.studioDescription.length < 50) {
        newErrors.studioDescription = 'Please provide at least 50 characters';
      }

      if (!formData.teamSize) {
        newErrors.teamSize = 'Team size is required';
      }
    }

    // Terms validation
    if (!formData.agreeToTerms) {
      newErrors.agreeToTerms = 'You must agree to the terms and conditions';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="space-y-4">
        <Input
          label="Email Address"
          type="email"
          placeholder="Enter your email"
          value={formData.email}
          onChange={(e) => handleInputChange('email', e.target.value)}
          error={errors.email}
          required
          disabled={isLoading}
        />

        <Input
          label="Display Name (Optional)"
          type="text"
          placeholder="Choose a display name"
          value={formData.displayName}
          onChange={(e) => handleInputChange('displayName', e.target.value)}
          description="This will be shown to other players"
          disabled={isLoading}
        />

        {/* Account Type Selection */}
        <div className="space-y-3">
          <label className="text-sm font-medium text-foreground">Choose Your Account Type</label>

          {/* Free Access Notice */}
          <FreeAccessNotice variant="full" />

          <div className="grid grid-cols-1 gap-3">
            {/* Free Account Option */}
            <div 
              className={`p-4 border rounded-lg cursor-pointer transition-all ${
                formData.accountType === 'free' 
                  ? 'border-green-500 bg-green-500/10' 
                  : 'border-border hover:border-green-500/50'
              }`}
              onClick={() => handleInputChange('accountType', 'free')}
            >
              <div className="flex items-start space-x-3">
                <div className={`w-4 h-4 rounded-full border-2 mt-0.5 ${
                  formData.accountType === 'free' 
                    ? 'border-green-500 bg-green-500' 
                    : 'border-muted-foreground'
                }`}>
                  {formData.accountType === 'free' && (
                    <div className="w-2 h-2 bg-white rounded-full m-0.5"></div>
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-1">
                    <h4 className="font-medium text-foreground">Free Account</h4>
                    <span className="text-xs px-2 py-1 bg-green-500/20 text-green-600 rounded-full">
                      Free Forever
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-2">
                    Perfect for casual gaming
                  </p>
                  <ul className="text-xs text-muted-foreground space-y-1">
                    <li>• Access to all free games</li>
                    <li>• Save game progress</li>
                    <li>• Create favorites list</li>
                    <li>• Rate and review games</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Premium Account Option */}
            <div
              className={`p-4 border rounded-lg cursor-pointer transition-all ${
                formData.accountType === 'premium'
                  ? 'border-primary bg-primary/10'
                  : 'border-border hover:border-primary/50'
              }`}
              onClick={() => handleInputChange('accountType', 'premium')}
            >
              <div className="flex items-start space-x-3">
                <div className={`w-4 h-4 rounded-full border-2 mt-0.5 ${
                  formData.accountType === 'premium'
                    ? 'border-primary bg-primary'
                    : 'border-muted-foreground'
                }`}>
                  {formData.accountType === 'premium' && (
                    <div className="w-2 h-2 bg-white rounded-full m-0.5"></div>
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-1">
                    <h4 className="font-medium text-foreground">Premium Account</h4>
                    <span className="text-xs px-2 py-1 bg-primary/20 text-primary rounded-full">
                      7-Day Free Trial
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-2">
                    Unlimited gaming experience
                  </p>
                  <ul className="text-xs text-muted-foreground space-y-1">
                    <li>• Access to 500+ games</li>
                    <li>• Ad-free experience</li>
                    <li>• Play on unlimited devices</li>
                    <li>• New games added weekly</li>
                    <li>• Priority customer support</li>
                  </ul>
                  <div className="mt-2 pt-2 border-t border-border">
                    <p className="text-xs font-medium text-primary">$5.00/month after trial</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Studio Account Option */}
            <div
              className={`p-4 border rounded-lg cursor-pointer transition-all ${
                formData.accountType === 'studio'
                  ? 'border-blue-500 bg-blue-500/10'
                  : 'border-border hover:border-blue-500/50'
              }`}
              onClick={() => handleInputChange('accountType', 'studio')}
            >
              <div className="flex items-start space-x-3">
                <div className={`w-4 h-4 rounded-full border-2 mt-0.5 ${
                  formData.accountType === 'studio'
                    ? 'border-blue-500 bg-blue-500'
                    : 'border-muted-foreground'
                }`}>
                  {formData.accountType === 'studio' && (
                    <div className="w-2 h-2 bg-white rounded-full m-0.5"></div>
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-1">
                    <h4 className="font-medium text-foreground">Studio Account</h4>
                    <span className="text-xs px-2 py-1 bg-blue-500/20 text-blue-600 rounded-full">
                      Requires Approval
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-2">
                    For game developers and studios
                  </p>
                  <ul className="text-xs text-muted-foreground space-y-1">
                    <li>• Publish and manage games</li>
                    <li>• Access SDK and API</li>
                    <li>• Revenue share program</li>
                    <li>• Analytics and reporting</li>
                    <li>• Dedicated support</li>
                  </ul>
                  <div className="mt-2 pt-2 border-t border-border">
                    <p className="text-xs font-medium text-blue-600">Free • Admin approval required</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Studio-specific fields */}
        {formData.accountType === 'studio' && (
          <div className="space-y-4 p-4 border border-blue-500/30 rounded-lg bg-blue-500/5">
            <h4 className="text-sm font-medium text-foreground">Studio Information</h4>

            <Input
              label="Studio Name"
              type="text"
              placeholder="Enter your studio name"
              value={formData.studioName}
              onChange={(e) => handleInputChange('studioName', e.target.value)}
              error={errors.studioName}
              required
              disabled={isLoading}
            />

            <Input
              label="Studio Website"
              type="url"
              placeholder="https://yourstudio.com"
              value={formData.studioWebsite}
              onChange={(e) => handleInputChange('studioWebsite', e.target.value)}
              error={errors.studioWebsite}
              description="Optional - Your studio's official website"
              disabled={isLoading}
            />

            <div>
              <label className="text-sm font-medium text-foreground block mb-2">
                Studio Description
              </label>
              <textarea
                placeholder="Tell us about your studio and the games you develop..."
                value={formData.studioDescription}
                onChange={(e) => handleInputChange('studioDescription', e.target.value)}
                className="flex min-h-[80px] w-full rounded-md border border-input bg-white text-black px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                disabled={isLoading}
              />
              {errors.studioDescription && (
                <p className="text-sm text-destructive mt-1">{errors.studioDescription}</p>
              )}
            </div>

            <div>
              <label className="text-sm font-medium text-foreground block mb-2">
                Team Size
              </label>
              <select
                value={formData.teamSize}
                onChange={(e) => handleInputChange('teamSize', e.target.value)}
                className="flex h-10 w-full rounded-md border border-input bg-white text-black px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                disabled={isLoading}
              >
                <option value="">Select team size</option>
                <option value="solo">Solo Developer</option>
                <option value="2-5">2-5 people</option>
                <option value="6-10">6-10 people</option>
                <option value="11-50">11-50 people</option>
                <option value="51+">51+ people</option>
              </select>
              {errors.teamSize && (
                <p className="text-sm text-destructive mt-1">{errors.teamSize}</p>
              )}
            </div>

            <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-md p-3">
              <p className="text-xs text-blue-800 dark:text-blue-200">
                Your studio account will be reviewed by our team. You'll receive an email once approved and can start publishing games.
              </p>
            </div>
          </div>
        )}

        <div className="relative">
          <Input
            label="Password"
            type={showPassword ? "text" : "password"}
            placeholder="Create a strong password"
            value={formData.password}
            onChange={(e) => handleInputChange('password', e.target.value)}
            error={errors.password}
            required
            disabled={isLoading}
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-9 text-muted-foreground hover:text-foreground transition-colors"
            disabled={isLoading}
          >
            {showPassword ? '👁️' : '👁️‍🗨️'}
          </button>
        </div>

        <div className="relative">
          <Input
            label="Confirm Password"
            type={showConfirmPassword ? "text" : "password"}
            placeholder="Confirm your password"
            value={formData.confirmPassword}
            onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
            error={errors.confirmPassword}
            required
            disabled={isLoading}
          />
          <button
            type="button"
            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            className="absolute right-3 top-9 text-muted-foreground hover:text-foreground transition-colors"
            disabled={isLoading}
          >
            {showConfirmPassword ? '👁️' : '👁️‍🗨️'}
          </button>
        </div>
      </div>

      <div className="space-y-4">
        <Checkbox
          label="I agree to the Terms of Service and Privacy Policy"
          checked={formData.agreeToTerms}
          onChange={(e) => handleInputChange('agreeToTerms', e.target.checked)}
          error={errors.agreeToTerms}
          required
          disabled={isLoading}
        />

        <Button
          type="submit"
          variant="default"
          size="lg"
          fullWidth
          loading={isLoading}
          disabled={isLoading}
          className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold"
        >
          Create Account
        </Button>
      </div>

      <div className="text-center text-sm text-muted-foreground">
        <p>
          Already have an account?{' '}
          <a href="/user-login" className="text-primary hover:text-primary/80 font-medium transition-colors">
            Sign in here
          </a>
        </p>
      </div>
    </form>
  );
};

export default RegistrationForm;
